import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import type { EChartsOption } from 'echarts';
import { debounce } from 'lodash';

interface DataItem {
  日期: string;
  产品编码: string;
  产品名称: string;
  销量: number;
  销售金额: number;
  价格: number;
}

interface PriceDailySalesChartProps {
  data: DataItem[];
  loading?: boolean;
}

export const PriceDailySalesChart: React.FC<PriceDailySalesChartProps> = ({ data, loading }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  // 处理数据
  const processData = (rawData: DataItem[]) => {
    if (!rawData || rawData.length === 0) return {
      dates: [],
      sales: [],
      prices: [],
      productInfo: null,
      priceRange: { min: 0, max: 100 }
    };

    // 按日期排序
    const sortedData = [...rawData].sort((a, b) => a.日期.localeCompare(b.日期));
    const prices = sortedData.map(item => item.价格);

    // 计算价格的最小值和最大值
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    // 计算价格范围，给上下留出15%的空间，让曲线变化更明显
    const priceBuffer = (maxPrice - minPrice) * 0.15 || 10; // 如果价格没有变化，使用固定buffer

    return {
      dates: sortedData.map(item => item.日期),
      sales: sortedData.map(item => item.销量),
      prices: prices,
      productInfo: sortedData[0] || null,
      priceRange: {
        min: Math.floor(minPrice - priceBuffer),
        max: Math.ceil(maxPrice + priceBuffer)
      }
    };
  };

  // 初始化图表
  const initChart = () => {
    if (!chartRef.current) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const { dates, sales, prices, productInfo, priceRange } = processData(data);

    const option: EChartsOption = {
      title: {
        text: '',
        left: 'center',
        top: 0,
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold'
        },
        subtextStyle: {
          fontSize: 12,
          color: '#666'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        },
        formatter: (params: any[]) => {
          if (!params[0] || !productInfo) return '';

          let result = `
            <div class="font-bold mb-2">${params[0].axisValue}</div>
            <div class="mb-2">
              <div>产品编码：${productInfo.产品编码}</div>
              <div>产品名称：${productInfo.产品名称}</div>
            </div>
          `;

          params.forEach(param => {
            const marker = `<span style="display:inline-block;margin-right:4px;border-radius:50%;width:10px;height:10px;background-color:${param.color};"></span>`;
            const value = param.seriesName === '价格'
              ? `¥${param.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : param.value.toLocaleString();
            result += `<div>${marker}${param.seriesName}：${value}</div>`;
          });

          return result;
        }
      },
      legend: {
        data: ['销量', '价格'],
        top: 30,
        left: 'center'
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
        top: 100
      },
      xAxis: {
        type: 'category',
        data: dates,
        axisLabel: {
          rotate: 45,
          formatter: (value: string) => {
            // 格式化日期显示，如 "2025-10-09" 显示为 "10月9日"
            const date = new Date(value);
            return `${date.getMonth() + 1}月${date.getDate()}日`;
          }
        },
        boundaryGap: false
      },
      yAxis: [
        {
          type: 'value',
          name: '销量',
          position: 'left',
          axisLabel: {
            formatter: (value: number) => value.toLocaleString()
          },
          splitLine: {
            lineStyle: {
              type: 'dashed'
            }
          }
        },
        {
          type: 'value',
          name: '价格',
          position: 'right',
          min: priceRange.min,
          max: priceRange.max,
          axisLabel: {
            formatter: (value: number) => `¥${value.toLocaleString()}`
          },
          splitLine: {
            show: false
          }
        }
      ],
      series: [
        {
          name: '销量',
          type: 'line',
          data: sales,
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: {
            color: '#5470C6'
          },
          lineStyle: {
            width: 2
          }
        },
        {
          name: '价格',
          type: 'line',
          yAxisIndex: 1,
          data: prices,
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: {
            color: '#EE6666'
          },
          lineStyle: {
            width: 2
          }
        }
      ]
    };

    chartInstance.current.setOption(option);
  };

  // 处理窗口大小变化
  useEffect(() => {
    const handleResize = debounce(() => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    }, 300);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, []);

  // 更新图表数据
  useEffect(() => {
    if (data && data.length > 0) {
      initChart();
    }
  }, [data]);

  // 销毁图表实例
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="w-full h-[500px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div ref={chartRef} className="w-full h-[500px]" />
  );
};

export default PriceDailySalesChart;
